import { TableSkeleton } from './feedback/LoadingState';
import React, { useEffect, useState } from 'react';
import {
  getTimelineEntryContent,
  getTimelineEntryKind,
  getTimelineTechnicalDetails,
  shortenWecomId
} from '../utils/wecomChatLogs';
import WecomMarkdownContent from './WecomMarkdownContent';

const directionLabel = {
  incoming: '用户消息',
  outgoing: '机器人回复',
  action: 'Jira 操作',
  error: '错误'
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('zh-CN', { hour12: false });
};

export const copyText = async (value) => {
  const text = String(value || '');
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // Fall through to the legacy browser copy path.
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = typeof document.execCommand === 'function'
    ? document.execCommand('copy')
    : false;
  document.body.removeChild(textarea);
  return copied;
};

export const CopyButton = ({ value, label }) => (
  <button
    type="button"
    className="wecom-icon-button"
    aria-label={`复制${label}`}
    title={`复制${label}`}
    onClick={(event) => {
      event.stopPropagation();
      void copyText(value);
    }}
  >
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
    </svg>
  </button>
);

const WecomConversationDrawer = ({
  open,
  conversation,
  logs,
  loading,
  error,
  hasMore,
  loadingOlder,
  onLoadOlder,
  onRetry,
  onClose
}) => {
  const [expandedLogIds, setExpandedLogIds] = useState([]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    setExpandedLogIds([]);
  }, [conversation?.bot_config_id, conversation?.conversation_id]);

  if (!open || !conversation) return null;

  const toggleExpanded = (logId) => {
    setExpandedLogIds((current) => (
      current.includes(logId)
        ? current.filter((id) => id !== logId)
        : [...current, logId]
    ));
  };

  return (
    <div
      className="wecom-drawer-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className="wecom-conversation-drawer" aria-label="会话详情">
        <header className="wecom-drawer-header">
          <div className="wecom-drawer-heading">
            <h2>
              {conversation.wecom_name || shortenWecomId(conversation.wecom_user_id)}
            </h2>
            <div className="wecom-drawer-meta">
              <span>
                {conversation.bot_name
                  || conversation.bot_id
                  || `Bot ${conversation.bot_config_id}`}
              </span>
              <span>{formatDateTime(conversation.last_active_at)}</span>
            </div>
            <div className="wecom-id-line">
              <span title={conversation.wecom_user_id}>
                {shortenWecomId(conversation.wecom_user_id)}
              </span>
              <CopyButton value={conversation.wecom_user_id} label="用户 ID" />
            </div>
            <div className="wecom-id-line">
              <span className="wecom-full-id">{conversation.conversation_id}</span>
              <CopyButton value={conversation.conversation_id} label="会话 ID" />
            </div>
          </div>
          <button
            type="button"
            className="wecom-icon-button"
            onClick={onClose}
            aria-label="关闭会话详情"
            title="关闭"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="wecom-drawer-body">
          {loading ? (
            <TableSkeleton rowCount={5} columnCount={2} label="加载会话详情中" />
          ) : error ? (
            <div className="wecom-detail-state wecom-detail-error">
              <span>错误：{error}</span>
              <button
                type="button"
                className="btn btn-secondary"
                data-action="retry-detail"
                onClick={onRetry}
              >
                重试
              </button>
            </div>
          ) : logs.length === 0 ? (
            <div className="wecom-detail-state">该会话暂无日志。</div>
          ) : (
            <>
              <div className="wecom-timeline">
                {logs.map((log) => {
                  const kind = getTimelineEntryKind(log);
                  const content = getTimelineEntryContent(log);
                  const technicalDetails = getTimelineTechnicalDetails(log);
                  const isLong = content.split('\n').length > 6 || content.length > 360;
                  const expanded = expandedLogIds.includes(log.id);
                  return (
                    <article
                      key={log.id}
                      className={`wecom-timeline-entry wecom-timeline-entry-${kind}`}
                    >
                      <div className="wecom-timeline-heading">
                        <strong>{directionLabel[kind]}</strong>
                        <time>{formatDateTime(log.created_at)}</time>
                      </div>
                      <div
                        className={[
                          'wecom-timeline-content',
                          isLong && !expanded ? 'is-collapsed' : '',
                          expanded ? 'is-expanded' : ''
                        ].filter(Boolean).join(' ')}
                      >
                        <WecomMarkdownContent content={content} />
                      </div>
                      {isLong && (
                        <button
                          type="button"
                          className="wecom-text-command"
                          data-expand-log-id={log.id}
                          onClick={() => toggleExpanded(log.id)}
                        >
                          {expanded ? '收起' : '展开'}
                        </button>
                      )}
                      {(log.issue_key || log.status === 'failed') && (
                        <div className="wecom-timeline-tags">
                          {log.issue_key && <span>{log.issue_key}</span>}
                          {log.status === 'failed' && (
                            <span className="is-error">失败</span>
                          )}
                        </div>
                      )}
                      {technicalDetails && (
                        <details className="wecom-technical-details">
                          <summary>技术详情</summary>
                          <pre>{JSON.stringify(technicalDetails, null, 2)}</pre>
                        </details>
                      )}
                    </article>
                  );
                })}
              </div>
              {hasMore && (
                <button
                  type="button"
                  className="btn btn-secondary wecom-load-older"
                  data-action="load-older"
                  disabled={loadingOlder}
                  onClick={onLoadOlder}
                >
                  {loadingOlder ? '加载中...' : '加载更早'}
                </button>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default WecomConversationDrawer;
